import React, { useEffect, useState } from 'react';
import {
    Box,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Select,
    Button,
    Flex,
    Heading,
    Card,
    useToast,
    Icon,
    Input
} from "@chakra-ui/react";
import { BellIcon, DownloadIcon } from "@chakra-ui/icons";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import * as XLSX from "xlsx";
import axios from "axios";
import * as signalR from "@microsoft/signalr";

// Custom input component for DatePicker with proper styling
const CustomDateInput = React.forwardRef(({ value, onClick }, ref) => (
    <Input
        value={value}
        ref={ref}
        onClick={onClick}
        readOnly
        cursor="pointer"
        border="1px solid"
        borderColor="gray.300"
        _hover={{ borderColor: "blue.400" }}
        _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
    />
));

// Change from default export to named export
export function EventLog() {
    const [tArray, setTArray] = useState([]);
    const [retry, setRetry] = useState(false);
    const [pageCount, setPageCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedType, setSelectedType] = useState('0');
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [recordsDetails, setRecordsDetails] = useState([]);
    const toast = useToast();

    const itemsPerPage = 10;

    const handleSelectedType = (event) => {
        setSelectedType(event.target.value);
    };

    const handleStartDateChange = (date) => {
        setStartDate(date);
    };

    const handleEndDateChange = (date) => {
        setEndDate(date);
    };

    useEffect(() => {
        const httpClient = axios.create({
            baseURL: 'https://villarhapsodyiot.org',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        const getData = async () => {
            try {
                const connectionPath = '/api/villarhapastrecord/1/10/';
                const response = await httpClient.get(connectionPath);

                let mArray = response.data.map((item) => ({
                    Id: item.item,
                    Label: item.label,
                    Status: item.status,
                    DataType: item.dataType,
                    Timestamp: item.timestamp,
                    Acknowledge: item.acknowledge,
                    Name: item.name
                }));

                setTArray(mArray);
            } catch (error) {
                console.error(error);
                setRetry(true);
                toast({
                    title: "Error fetching data",
                    description: "Failed to load event log data",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            }
        };

        const getCount = async () => {
            try {
                const connectionPath = '/api/villarhapastrecord/count';
                const response = await httpClient.get(connectionPath);
                setPageCount(response.data);
            } catch (error) {
                console.error(error);
                setRetry(true);
                toast({
                    title: "Error fetching count",
                    description: "Failed to load page count",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            }
        };

        getCount();
        getData();
    }, []);

    useEffect(() => {
        const httpClient = axios.create({
            baseURL: 'https://villarhapsodyiot.org',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        const getData = async () => {
            try {
                const connectionPath = '/api/villarhapastrecord/' + currentPage.toString() + '/10/';
                const response = await httpClient.get(connectionPath);

                let mArray = response.data.map((item) => ({
                    Id: item.item,
                    Label: item.label,
                    Status: item.status,
                    DataType: item.dataType,
                    Timestamp: item.timestamp,
                    Acknowledge: item.acknowledge,
                    Name: item.name
                }));

                setTArray(mArray);
            } catch (error) {
                console.error(error);
                setRetry(true);
                toast({
                    title: "Error fetching page data",
                    description: "Failed to load page " + currentPage,
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            }
        };

        getData();
    }, [currentPage]);

    const exportToExcel = (data, fileName) => {
        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
        XLSX.writeFile(workbook, `${fileName}.xlsx`);
    };

    const handleSearchClick = () => {
        fetch('https://villarhapsodyiot.org/api/villarhapastrecord/filter/', {
            method: 'POST',
            headers: {
                'Accept': 'application/json, text/plain',
                'Content-Type': 'application/json;charset=UTF-8'
            },
            body: JSON.stringify({
                type: selectedType,
                startdate: startDate,
                enddate: endDate,
            })
        })
            .then(response => response.json())
            .then(data => {
                let mArray = data.map((item) => ({
                    Id: item.item,
                    Label: item.label,
                    Status: item.status,
                    DataType: item.dataType,
                    Timestamp: item.timestamp,
                    Acknowledge: item.acknowledge,
                    Name: item.name
                }));

                setTArray(mArray);
                exportToExcel(data, 'EventLogData');
            })
            .catch((error) => {
                console.error('Error:', error);
                toast({
                    title: "Search failed",
                    description: "An error occurred during search",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            });
    };

    const Pagination = () => {
        const maxPage = Math.ceil(pageCount / itemsPerPage);
        const getPaginationGroup = () => {
            let start = Math.floor((currentPage - 1) / 5) * 5;
            return new Array(5).fill().map((_, idx) => start + idx + 1);
        };

        return (
            <Flex justify="center" mt={4}>
                <Button
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    mr={2}
                >
                    Prev
                </Button>

                {getPaginationGroup().map((item, index) => (
                    <Button
                        key={index}
                        onClick={() => setCurrentPage(item)}
                        disabled={item > maxPage}
                        colorScheme={item === currentPage ? "blue" : "gray"}
                        mx={1}
                    >
                        {item}
                    </Button>
                ))}

                <Button mx={2} variant="outline">
                    Total {maxPage} Pages
                </Button>

                <Button
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, maxPage))}
                    disabled={currentPage === maxPage}
                    ml={2}
                >
                    Next
                </Button>
            </Flex>
        );
    };

    return (
        <Box m="20px">
            <Card p={4} mb={4}>
                <Flex align="center" mb={4}>
                    <Icon as={BellIcon} boxSize={6} mr={2} />
                    <Heading size="lg">Event Log</Heading>
                </Flex>

                <Flex direction={["column", "column", "row"]} mb={4} gap={4} alignItems="flex-end">
                    <Box flex="1">
                        <Box mb={2}>Alert Type</Box>
                        <Select value={selectedType} onChange={handleSelectedType}>
                            <option value="0">All Alerts</option>
                            <option value="16">Road Management</option>
                            <option value="2">Door Monitoring</option>
                            <option value="5">Flood Monitoring</option>
                            <option value="9">Garbage/Recycling Bin Capacity Monitoring</option>
                        </Select>
                    </Box>

                    <Box flex="1">
                        <Box mb={2}>Start Time</Box>
                        <DatePicker
                            selected={startDate}
                            onChange={handleStartDateChange}
                            showTimeSelect
                            timeFormat="HH:mm"
                            timeIntervals={15}
                            dateFormat="dd-MM-yyyy HH:mm"
                            timeCaption="time"
                            customInput={<CustomDateInput />}
                        />
                    </Box>

                    <Box flex="1">
                        <Box mb={2}>End Time</Box>
                        <DatePicker
                            selected={endDate}
                            onChange={handleEndDateChange}
                            showTimeSelect
                            timeFormat="HH:mm"
                            timeIntervals={15}
                            dateFormat="dd-MM-yyyy HH:mm"
                            timeCaption="time"
                            customInput={<CustomDateInput />}
                        />
                    </Box>

                    <Box flex="1">
                        <Button
                            colorScheme="blue"
                            onClick={handleSearchClick}
                            leftIcon={<DownloadIcon />}
                            width="full"
                        >
                            Search & Export
                        </Button>
                    </Box>
                </Flex>
            </Card>

            <Card p={4}>
                <Box overflowX="auto">
                    <Table variant="simple">
                        <Thead>
                            <Tr>
                                <Th>Alert Name</Th>
                                <Th>Alert Status</Th>
                                <Th>Created Time</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {Array.isArray(tArray) && tArray.map((item, index) => (
                                <Tr key={index}>
                                    <Td>{item.Label}</Td>
                                    <Td>{item.Status}</Td>
                                    <Td>{item.Timestamp}</Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </Box>

                <Pagination />
            </Card>
        </Box>
    );
}

// Add this line to ensure both default and named exports work
export default EventLog;